import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { Grid, TextField } from "@material-ui/core";
import { ErrorModal } from "@dexteel/mesf-core";
import LazyLoading from "@/components/loaders/LazyLoading/LazyLoading";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import type { TemplateLOTO } from "../../../../models/TemplateLOTO";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

interface Props {
	templateLOTO: TemplateLOTO[];
	onSelectionChanged: (selectedLOTO: TemplateLOTO[]) => void;
}

export const AssetLOTOTable = ({ templateLOTO, onSelectionChanged }: Props) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const {
		state: { assetLOTO },
	} = useTemplatesContext();

	const handlerFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFilterInput(e.target.value);
	};

	const sortRows = () => {
		gridApi?.applyColumnState({
			state: [{ colId: "LOTONumber", sort: "asc", sortIndex: 0 }],
		});
	};

	const loadingOverlayComponent = useMemo(() => LazyLoading, []);

	const rows = useMemo(() => {
		return (assetLOTO as TemplateLOTO[]).map(
			(LOTO: TemplateLOTO, index: number) => ({
				...LOTO,
				id: `${LOTO.LOTONumber}-${index}`,
				isSelected: templateLOTO.some(
					(wdLOTO: TemplateLOTO) =>
						wdLOTO.LOTONumber === LOTO.LOTONumber && wdLOTO.IsAssetResource,
				),
			}),
		);
	}, [assetLOTO, templateLOTO]);

	const columns: ColDef[] = [
		{
			headerName: "",
			flex: 1,
			maxWidth: 100,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			sortIndex: 1,
		},
		{
			field: "LOTONumber",
			headerName: "LOTO ID",
			flex: 3,
			sortIndex: 1,
			minWidth: 200,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.LOTONumber?.localeCompare(nodeB.data.LOTONumber);
				}
			},
		},
		{
			field: "LOTOName",
			headerName: "LOTO Name",
			flex: 8,
			minWidth: 200,
			sortIndex: 0,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.LOTOName?.localeCompare(nodeB.data.LOTOName);
				}
			},
		},
	];

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows();
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(() => {
		gridApi?.forEachNode((node) => {
			if (node.data.isSelected) {
				node.setSelected(true);
			}
		});
		sortRows();
	}, [gridApi, templateLOTO]);

	useEffect(() => {
		gridApi?.setGridOption("quickFilterText", filterInput.trim());
	}, [filterInput, gridApi]);

	const onGridReady = (event: GridReadyEvent) => {
		setGridApi(event.api);
	};

	return (
		<>
			<Grid
				container
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid item xs={12}>
					<TextField
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						fullWidth
					/>
				</Grid>
			</Grid>
			<div style={{ width: "100%", height: "45vh" }}>
				<AgGridReact
					getRowId={(params) => params.data.id}
					rowHeight={30}
					headerHeight={25}
					animateRows={true}
					onGridReady={onGridReady}
					rowData={rows}
					columnDefs={columns}
					defaultColDef={globalDefaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					loadingOverlayComponent={loadingOverlayComponent}
					rowSelection="multiple"
					onSelectionChanged={onAgGridSelectionChanged}
					suppressContextMenu={true}
					loading={isLoading}
				/>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
